import * as React from "react";
import { TrendingUp, TrendingDown } from "lucide-react";
import {
  Card,
  CardHeader,
  CardContent,
  CardTitle,
} from "./card";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "./table";
import { CurrencyValue, PercentValue } from "./value-display";
import { Text, MonoText } from "./typography";

/**
 * TopPerformers Component
 * Displays top 5 and bottom 5 performers side by side
 *
 * @param {Object} props
 * @param {Array} props.topPerformers - Array of top performing holdings
 * @param {Array} props.bottomPerformers - Array of worst performing holdings
 * @param {boolean} props.loading - Loading state
 */
export function TopPerformers({ topPerformers = [], bottomPerformers = [], loading = false }) {
  const renderPerformerTable = (performers, title, isTop) => (
    <Card>
      <CardHeader className="pb-2 pt-3 px-4">
        <div className="flex items-center gap-2">
          {isTop ? (
            <TrendingUp className="h-4 w-4 text-success" />
          ) : (
            <TrendingDown className="h-4 w-4 text-destructive" />
          )}
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-3">
        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={`skeleton-${i}`}
                className="h-10 rounded bg-muted animate-pulse"
              />
            ))}
          </div>
        ) : performers.length === 0 ? (
          <Text variant="secondary" size="sm" className="text-center py-4">
            No investment data available
          </Text>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8 px-2">#</TableHead>
                <TableHead className="px-2">Name</TableHead>
                <TableHead className="text-right px-2">Gain/Loss</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {performers.map((holding, index) => (
                <TableRow key={holding.holdingId || index}>
                  <TableCell className="py-2 px-2 w-8">
                    <MonoText size="sm" className="text-muted-foreground">
                      {index + 1}
                    </MonoText>
                  </TableCell>
                  <TableCell className="py-2 px-2">
                    <div className="max-w-[180px]">
                      <Text size="sm" className="font-medium truncate" title={holding.assetName}>
                        {holding.assetName}
                      </Text>
                      {holding.ticker && holding.ticker !== "-" && (
                        <MonoText size="xs" className="text-muted-foreground block truncate">
                          {holding.ticker}
                        </MonoText>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="py-2 px-2 text-right">
                    <div className="flex flex-col items-end">
                      <CurrencyValue
                        value={holding.gainEUR || 0}
                        currency="EUR"
                        size="sm"
                        colorize
                      />
                      <PercentValue
                        value={holding.gainPercent || 0}
                        size="xs"
                        showTrend
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {renderPerformerTable(topPerformers, "Top 5 Performers", true)}
      {renderPerformerTable(bottomPerformers, "Bottom 5 Performers", false)}
    </div>
  );
}

export default TopPerformers;
